import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { _Api } from "../api/_call";
import { ReportMenuModal } from "../common/modules/reportMenu";
import { toFormatPrice } from "../common/helpers/function";
import { formatDate } from "../common/helpers/formatDate";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Pagination } from "../common/Pagination";
import { useTranslation } from "react-i18next";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import Select from "react-select";
import "./report.scss";

/* -----------------------------------------------------------------------------
 * Inventory Report — Stock Adjustment.
 *   list : api/v1/Inventory/report/stockAdjustment
 *          body { status, location_id, search, start_date, end_date, skip, limit }
 *          response { code, success, count, total, data: [{ sr_no, date, sa_no,
 *                     adjustment_id, SKU, stock_id, metal_name, stone_name,
 *                     size_name, qty, adj_qty, amount, amount_value, location,
 *                     status }] }
 * Custom client-side sorting (no jQuery DataTables), matching the Stock
 * Adjustment transaction list's convention.
 * ---------------------------------------------------------------------------*/
const LIST_ENDPOINT = "api/v1/Inventory/report/stockAdjustment";

const HEAD_COLUMNS = [
  { key: "date", label: "Date", sortable: true },
  { key: "sa_no", label: "SA No.", sortable: true },
  { key: "SKU", label: "SKU", sortable: true },
  { key: "stock_id", label: "Stock ID", sortable: true },
  { key: "metal_name", label: "Metal", sortable: true },
  { key: "stone_name", label: "Stone", sortable: true },
  { key: "size_name", label: "Size", sortable: true },
  { key: "qty", label: "Qty", sortable: true },
  { key: "adj_qty", label: "Adj Qty", sortable: true },
  { key: "amount", label: "Amount", sortable: true },
  { key: "location", label: "Location", sortable: true },
  { key: "status", label: "Status", sortable: true },
];

const SORT_ACCESSORS = {
  date: (r) => r?.date,
  sa_no: (r) => r?.sa_no,
  SKU: (r) => r?.SKU,
  stock_id: (r) => r?.stock_id,
  metal_name: (r) => r?.metal_name,
  stone_name: (r) => r?.stone_name,
  size_name: (r) => r?.size_name,
  qty: (r) => Number(r?.qty) || 0,
  adj_qty: (r) => Number(r?.adj_qty) || 0,
  amount: (r) => Number(r?.amount_value) || 0,
  location: (r) => r?.location,
  status: (r) => r?.status,
};

const STATUS_CLASS = {
  pending: "yellow-text",
  approved: "status-green-text",
  reverted: "danger-text",
};

const STATUS_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Reverted", value: "reverted" },
];

const StockAdjustmentReport = () => {
  const { t } = useTranslation();
  const base_assets = process.env.REACT_APP_BASE_ASSETS_URL;
  const dataFetched = useRef();
  const stickyTopRef = useRef(null);
  const tableHeadScrollRef = useRef(null);
  const tableBodyScrollRef = useRef(null);
  const tableHeadTableRef = useRef(null);
  const tableBodyTableRef = useRef(null);
  const tableScrollSyncLock = useRef(false);

  const [limit, setLimit] = useState(100);
  const [filtername, setFiltername] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_OPTIONS[0]);
  const [reportlist, setReportlist] = useState([]);
  const [datalodaer, setDatsLodaer] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [state, setstate] = useState({
    totalPages: 0,
    currentPage: 1,
  });
  const { totalPages, currentPage } = state;
  const [datefilter, setDatefilter] = useState(() => {
    const start = new Date();
    start.setDate(start.getDate() - 30);
    start.setHours(0, 0, 0, 0);
    return [start, new Date()];
  });
  const [datelable, setDatelable] = useState("LastMonth");
  const datePickerRef = useRef(null);

  const selectOptions = [
    { label: 100, value: 100 },
    { label: 200, value: 200 },
    { label: 300, value: 300 },
  ];

  const changeDates = (lable) => {
    const MainDate = new Date();
    if (lable === "Today") {
      const s = new Date();
      s.setHours(0, 0, 0, 0);
      setDatefilter([s, new Date()]);
      setDatelable("Today");
    } else if (lable === "Yesterday") {
      const s = new Date(MainDate.setDate(MainDate.getDate() - 1));
      s.setHours(0, 0, 0, 0);
      const e = new Date(s);
      e.setHours(23, 59, 59, 999);
      setDatefilter([s, e]);
      setDatelable("Yesterday");
    } else if (lable === "7days") {
      const s = new Date(MainDate.setDate(MainDate.getDate() - 7));
      s.setHours(0, 0, 0, 0);
      const e = new Date();
      e.setHours(23, 59, 59, 999);
      setDatefilter([s, e]);
      setDatelable("7days");
    } else if (lable === "30days") {
      const s = new Date(MainDate.setDate(MainDate.getDate() - 30));
      s.setHours(0, 0, 0, 0);
      const e = new Date();
      e.setHours(23, 59, 59, 999);
      setDatefilter([s, e]);
      setDatelable("30days");
    } else if (lable === "ThisMonth") {
      const s = new Date(MainDate.getFullYear(), MainDate.getMonth(), 1);
      s.setHours(0, 0, 0, 0);
      setDatefilter([s, new Date()]);
      setDatelable("ThisMonth");
    } else if (lable === "LastMonth") {
      const s = new Date(MainDate.getFullYear(), MainDate.getMonth() - 1, 1);
      s.setHours(0, 0, 0, 0);
      const e = new Date(MainDate.getFullYear(), MainDate.getMonth(), 0);
      e.setHours(23, 59, 59, 999);
      setDatefilter([s, e]);
      setDatelable("LastMonth");
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const sortedList = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortConfig.key];
    if (!accessor) return reportlist;
    const sorted = [...reportlist].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av === undefined || av === null) return 1;
      if (bv === undefined || bv === null) return -1;
      if (typeof av === "number" && typeof bv === "number") return av - bv;
      return String(av).localeCompare(String(bv), undefined, { numeric: true });
    });
    return sortConfig.direction === "asc" ? sorted : sorted.reverse();
  }, [reportlist, sortConfig]);

  const getReport = useCallback(
    async (current, firstlimit, search, statusValue) => {
      const tablelimit = firstlimit ? firstlimit : limit;
      const skipNumber = current ? (current - 1) * (Number(tablelimit) || 0) : 0;
      setDatsLodaer(true);
      try {
        const postdata = {
          status: statusValue !== undefined ? statusValue : statusFilter.value,
          search: search === "" ? search : (search || filtername || ""),
          limit: firstlimit ? firstlimit : limit,
          skip: skipNumber,
          start_date: datefilter ? datefilter[0] : "",
          end_date: datefilter ? datefilter[1] : "",
        };
        let res = await _Api(postdata, LIST_ENDPOINT);
        if (res?.code === 200) {
          const rows = res?.data || [];
          const page = current ? Number(current) : 1;
          const pageini = Number(postdata.limit) || rows.length || 1;
          const hasApiTotal = res?.total !== undefined && res?.total !== null && res?.total !== "";
          setReportlist(rows);
          if (!rows.length && page > 1 && !hasApiTotal) {
            setstate((prev) => ({
              ...prev,
              totalPages: page - 1,
              currentPage: page - 1,
            }));
          } else {
            setstate((prev) => {
              let totalPages = 0;
              if (hasApiTotal) {
                totalPages = Math.ceil((Number(res?.total) || 0) / pageini) || 0;
              } else if (!rows.length) {
                totalPages = 0;
              } else if (rows.length < pageini) {
                totalPages = page;
              } else if (page === 1) {
                totalPages = 2;
              } else {
                totalPages = Math.max(prev.totalPages || 0, page + 1);
              }
              return { ...prev, totalPages, currentPage: page };
            });
          }
        } else {
          setReportlist([]);
          setstate({ totalPages: 0, currentPage: 1 });
        }
      } catch (err) {
        console.log(err, "err");
      } finally {
        setDatsLodaer(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [limit, datefilter, statusFilter]
  );

  const filterbyname = (search) => {
    setstate((prev) => ({ ...prev, currentPage: 1 }));
    getReport(1, limit, search);
  };
  const getlimit = (nextLimit) => {
    setLimit(nextLimit);
    getReport(1, nextLimit);
    setstate((prev) => ({ ...prev, currentPage: 1 }));
  };
  const handlePaginations = (current) => {
    setstate((prev) => ({ ...prev, currentPage: current }));
    getReport(current, "");
  };

  useEffect(() => {
    if (dataFetched.current) return;
    dataFetched.current = true;
    getReport(1, limit);
  }, [getReport, limit]);

  useEffect(() => {
    if (!dataFetched.current) return;
    getReport(1, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datefilter, statusFilter]);

  useEffect(() => {
    const el = stickyTopRef.current;
    if (!el) return undefined;
    const page = el.closest(".stock-adjustment-page");
    const apply = () => {
      if (!page) return;
      page.style.setProperty(
        "--report-sticky-block-height",
        `${el.offsetHeight}px`,
      );
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    window.addEventListener("resize", apply);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, []);

  const syncStockAdjustmentTableScroll = (source) => {
    if (tableScrollSyncLock.current) return;
    const head = tableHeadScrollRef.current;
    const body = tableBodyScrollRef.current;
    if (!head || !body) return;
    tableScrollSyncLock.current = true;
    if (source === "head") {
      body.scrollLeft = head.scrollLeft;
    } else {
      head.scrollLeft = body.scrollLeft;
    }
    requestAnimationFrame(() => {
      tableScrollSyncLock.current = false;
    });
  };

  useEffect(() => {
    let frame = 0;
    const syncStockAdjustmentTableWidths = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const headTable = tableHeadTableRef.current;
        const bodyTable = tableBodyTableRef.current;
        if (!headTable || !bodyTable) return;

        const headThs = headTable.querySelectorAll("thead th");
        const bodyRow = bodyTable.querySelector("tbody tr");
        const count = headThs.length;
        if (!count) return;

        headTable.style.width = "max-content";
        bodyTable.style.width = "max-content";
        headTable.style.tableLayout = "auto";
        bodyTable.style.tableLayout = "auto";
        headThs.forEach((th) => {
          th.style.width = "";
          th.style.minWidth = "";
          th.style.maxWidth = "";
        });
        bodyTable.querySelectorAll("td, th").forEach((cell) => {
          cell.style.width = "";
          cell.style.minWidth = "";
          cell.style.maxWidth = "";
        });

        const widths = [];
        for (let i = 0; i < count; i += 1) {
          const hw = headThs[i].getBoundingClientRect().width;
          const bw = bodyRow?.children[i]?.getBoundingClientRect().width || 0;
          widths.push(Math.ceil(Math.max(hw, bw, 40)));
        }
        let total = widths.reduce((sum, w) => sum + w, 0);
        const containerWidth = Math.floor(
          tableBodyScrollRef.current?.clientWidth ||
            tableHeadScrollRef.current?.clientWidth ||
            0,
        );
        if (containerWidth > total && total > 0) {
          const scale = containerWidth / total;
          let used = 0;
          for (let i = 0; i < count; i += 1) {
            if (i === count - 1) {
              widths[i] = Math.max(widths[i], containerWidth - used);
            } else {
              widths[i] = Math.max(1, Math.floor(widths[i] * scale));
              used += widths[i];
            }
          }
          total = containerWidth;
        }
        headTable.style.tableLayout = "fixed";
        bodyTable.style.tableLayout = "fixed";
        headTable.style.width = `${total}px`;
        bodyTable.style.width = `${total}px`;

        for (let i = 0; i < count; i += 1) {
          const widthPx = `${widths[i]}px`;
          headThs[i].style.width = widthPx;
          headThs[i].style.minWidth = widthPx;
          headThs[i].style.maxWidth = widthPx;
          bodyTable
            .querySelectorAll(`tr > :nth-child(${i + 1})`)
            .forEach((cell) => {
              cell.style.width = widthPx;
              cell.style.minWidth = widthPx;
              cell.style.maxWidth = widthPx;
            });
        }
      });
    };

    syncStockAdjustmentTableWidths();
    window.addEventListener("resize", syncStockAdjustmentTableWidths);
    const observer = new ResizeObserver(syncStockAdjustmentTableWidths);
    if (tableHeadScrollRef.current) observer.observe(tableHeadScrollRef.current);
    if (tableBodyScrollRef.current) observer.observe(tableBodyScrollRef.current);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", syncStockAdjustmentTableWidths);
      observer.disconnect();
    };
  }, [sortedList, datalodaer, sortConfig]);

  const downloadexclsheet = () => {
    const header_values = [
      "#",
      "Date",
      "SA No.",
      "SKU",
      "Stock ID",
      "Metal",
      "Stone",
      "Size",
      "Qty",
      "Adj Qty",
      "Amount",
      "Location",
      "Status",
    ];
    const bodyarray = reportlist.map((r, key) => [
      key + 1,
      r?.date ? formatDate(r?.date) : "",
      r?.sa_no,
      r?.SKU,
      r?.stock_id,
      r?.metal_name,
      r?.stone_name,
      r?.size_name,
      r?.qty,
      r?.adj_qty,
      r?.amount,
      r?.location,
      r?.status,
    ]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(bodyarray, {
      origin: "A2",
      skipHeader: true,
    });
    XLSX.utils.sheet_add_aoa(ws, [header_values], { origin: "A1" });
    XLSX.utils.book_append_sheet(wb, ws, "Records");
    const fileContent = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([fileContent], {
      type: "application/octet-stream",
    });
    FileSaver.saveAs(data, "report_stock_adjustment.xlsx");
  };

  const totalQty = reportlist.reduce(
    (s, v) => s + (parseInt(v?.qty, 10) || 0),
    0
  );
  const totalAdjQty = reportlist.reduce(
    (s, v) => s + (parseInt(v?.adj_qty, 10) || 0),
    0
  );
  const totalAmount = reportlist.reduce(
    (s, v) => s + (Number(v?.amount_value) || 0),
    0
  );
  const uniqueSkuCount = new Set(
    reportlist.map((v) => v?.SKU).filter(Boolean)
  ).size;
  const uniqueLocationCount = new Set(
    reportlist.map((v) => v?.location).filter(Boolean)
  ).size;

  return (
    <React.Fragment>
      <div className="main-body report-page stock-adjustment-page product-tab-page-padding-top">
        <div className="container">
          <div className="row">
            <div className="col-lg-12" style={{ display: "flex", flexWrap: "wrap" }}>
              <div className="sticky-top-report-page" ref={stickyTopRef}>
              <h2 className="main-body-heading">
                <span>
                  {t("report")} {">"}
                </span>
                &nbsp;&nbsp;{t("Stock Adjustment")}
              </h2>

              <div className="main-body-top-inventory">
                <div className="main-body-top-inventory-status">
                  <div className="main-body-top-inventory-status-card">
                    <div className="left-side">
                      <img
                        src={base_assets + "images/icons/free-stock.png"}
                        className="inventory-img"
                        alt="free-stock"
                      />
                    </div>
                    <div className="right-side">
                      <span className="inventory-name">{t("Total Adj Qty")}</span>
                      <h2 className="inventory-value stock">
                        {totalAdjQty ? totalAdjQty : "0"}
                      </h2>
                    </div>
                  </div>
                  <div className="main-body-top-inventory-status-card">
                    <div className="left-side">
                      <img
                        src={base_assets + "images/icons/sku.png"}
                        className="inventory-img"
                        alt="sku"
                      />
                    </div>
                    <div className="right-side">
                      <span className="inventory-name">{t("Sku")}</span>
                      <h2 className="inventory-value sku">
                        {uniqueSkuCount ? uniqueSkuCount : "0"}
                      </h2>
                    </div>
                  </div>
                  <div className="main-body-top-inventory-status-card">
                    <div className="left-side">
                      <img
                        src={base_assets + "images/icons/value.png"}
                        className="inventory-img"
                        alt="value"
                      />
                    </div>
                    <div className="right-side">
                      <span className="inventory-name">{t("Amount")}</span>
                      <h2 className="inventory-value value">
                        {toFormatPrice(totalAmount, { addSymbol: true })}
                      </h2>
                    </div>
                  </div>
                  <div className="main-body-top-inventory-status-card">
                    <div className="left-side">
                      <img
                        src={base_assets + "images/icons/location.png"}
                        className="inventory-img"
                        alt="location"
                      />
                    </div>
                    <div className="right-side">
                      <span className="inventory-name">{t("Location")}</span>
                      <h2 className="inventory-value location">
                        {uniqueLocationCount ? uniqueLocationCount : "0"}
                      </h2>
                    </div>
                  </div>
                </div>
              </div>

              <div className="main-body-top-status-bar">
                <ReportMenuModal />
                <Select
                  className="limit-select short z-10"
                  options={selectOptions}
                  value={{ label: limit, value: limit }}
                  onChange={(e) => {
                    setLimit(e.value);
                    getlimit(e.value);
                  }}
                  isSearchable={false}
                />
                <Select
                  className="limit-select short z-10 mx-2"
                  options={STATUS_OPTIONS}
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e);
                    setstate((prev) => ({ ...prev, currentPage: 1 }));
                  }}
                  isSearchable={false}
                />
                <div className="main-body-top-status-bar-filter">
                  <input
                    value={filtername}
                    type="text"
                    className="main-body-top-status-bar-filter-input"
                    placeholder={t("Search by SA No., SKU, Stock ID")}
                    onKeyPress={(e) =>
                      e.key === "Enter" && filterbyname(e.target.value)
                    }
                    onChange={(e) => setFiltername(e.target.value)}
                  />
                  {filtername ? (
                    <img
                      className="main-body-top-status-bar-filter-clear"
                      src={base_assets + "images/icons/False.png"}
                      onClick={() => {
                        setFiltername("");
                        filterbyname("");
                      }}
                      alt=""
                    />
                  ) : (
                    ""
                  )}
                  <button
                    onClick={() => filterbyname(filtername)}
                    className="main-body-top-status-bar-filter-search"
                  >
                    <i className="fa fa-search" aria-hidden="true" />
                  </button>
                </div>
                <div className="main-body-top-status-bar-multi-date-picker">
                  <div className="main-body-top-status-bar-note-button common-dropdown replenish-date">
                    <button
                      className="dropdown-toggle btn-style align-items-center d-flex"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 4.66667V8L10.5 10.5M15.5 8C15.5 12.1421 12.1421 15.5 8 15.5C3.85786 15.5 0.5 12.1421 0.5 8C0.5 3.85786 3.85786 0.5 8 0.5C12.1421 0.5 15.5 3.85786 15.5 8Z"
                          stroke="currentcolor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {t(datelable)}
                    </button>
                    <ul
                      className="dropdown-menu dropdown-menu-right-fix"
                      aria-labelledby="dropdownMenuButton1"
                    >
                      <li
                        className={`dropdown-item ${datelable === "Today" ? "active" : ""}`}
                        onClick={() => changeDates("Today")}
                      >
                        {t("Today")}
                      </li>
                      <li
                        className={`dropdown-item ${datelable === "Yesterday" ? "active" : ""}`}
                        onClick={() => changeDates("Yesterday")}
                      >
                        {t("Yesterday")}
                      </li>
                      <li
                        className={`dropdown-item ${datelable === "7days" ? "active" : ""}`}
                        onClick={() => changeDates("7days")}
                      >
                        {t("Last 7 days")}
                      </li>
                      <li
                        className={`dropdown-item ${datelable === "30days" ? "active" : ""}`}
                        onClick={() => changeDates("30days")}
                      >
                        {t("Last 30 days")}
                      </li>
                      <li
                        className={`dropdown-item ${datelable === "ThisMonth" ? "active" : ""}`}
                        onClick={() => changeDates("ThisMonth")}
                      >
                        {t("This Month")}
                      </li>
                      <li
                        className={`dropdown-item ${datelable === "LastMonth" ? "active" : ""}`}
                        onClick={() => changeDates("LastMonth")}
                      >
                        {t("Last Month")}
                      </li>
                      <li
                        className={`dropdown-item ${datelable === "Custom Range" ? "active" : ""}`}
                        onClick={() => datePickerRef.current.setOpen(true)}
                      >
                        {t("Custom Range")}
                      </li>
                    </ul>
                    <DatePicker
                      selectsRange={true}
                      monthsShown={2}
                      startDate={datefilter[0]}
                      endDate={datefilter[1]}
                      onChange={(update) => {
                        const startDate = update[0];
                        const endDate = update[1];
                        setDatelable("Custom Range");
                        if (startDate && endDate) {
                          const s = new Date(startDate);
                          s.setHours(0, 0, 0, 0);
                          const e = new Date(endDate);
                          e.setHours(23, 59, 59, 999);
                          setDatefilter([s, e]);
                        } else {
                          setDatefilter([startDate, endDate]);
                        }
                      }}
                      dateFormat="yyyy-MM-dd"
                      ref={datePickerRef}
                      autoComplete="none"
                    />
                  </div>
                  <div className="main-body-top-status-bar-print-export-dropdown dropdown">
                    <button
                      className="btn dropdown-toggle"
                      type="button"
                      id="dropdownMenuButton1"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <img
                        src={base_assets + "images/icons/ellipsis-circular.png"}
                        alt=""
                      />
                    </button>
                    <ul
                      className="dropdown-menu"
                      aria-labelledby="dropdownMenuButton1"
                    >
                      <li
                        className="dropdown-item"
                        onClick={() => downloadexclsheet()}
                      >
                        <div className="dropdown-item-img">
                          <img
                            src={base_assets + "images/icons/export-x-icon.png"}
                            alt=""
                          />
                        </div>
                        <span>{t("export")}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              </div>

              <div
                className="main-body-main-table-wrap position-relative report-sticky-table-wrap"
                style={{
                  maxWidth: "100%",
                  width: "100%",
                  overflow: "visible",
                }}
              >
                <div className="report-sticky-thead">
                  <div
                    className="report-table-hscroll report-table-hscroll--head"
                    ref={tableHeadScrollRef}
                    onScroll={() => syncStockAdjustmentTableScroll("head")}
                  >
                    <table
                      ref={tableHeadTableRef}
                      className="stripe row-border order-column common-table table-striped main-table report-head-table stock-adjustment-report-table"
                    >
                      <thead>
                        <tr>
                          <th>#</th>
                          {HEAD_COLUMNS.map((col) => {
                            const active = sortConfig.key === col.key;
                            return (
                              <th
                                key={col.key}
                                className={col.sortable ? "cursor-pointer" : ""}
                                onClick={
                                  col.sortable
                                    ? () => handleSort(col.key)
                                    : undefined
                                }
                              >
                                {t(col.label)}
                                {col.sortable ? (
                                  <div className="up-down-arrow-btn">
                                    <img
                                      className={
                                        "up-down-arrow-img up-down-arrow-img-forward" +
                                        (active && sortConfig.direction === "desc"
                                          ? ""
                                          : " active")
                                      }
                                      src={
                                        base_assets +
                                        "images/icons/up-down-arrow.png"
                                      }
                                      alt=""
                                    />
                                    <img
                                      className={
                                        "up-down-arrow-img up-down-arrow-img-reverse" +
                                        (active && sortConfig.direction === "desc"
                                          ? " active"
                                          : "")
                                      }
                                      src={
                                        base_assets +
                                        "images/icons/up-down-arrow-reverse.png"
                                      }
                                      alt=""
                                    />
                                  </div>
                                ) : null}
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                    </table>
                  </div>
                </div>
                <div
                    className="report-table-hscroll report-table-hscroll--body"
                    ref={tableBodyScrollRef}
                    onScroll={() => syncStockAdjustmentTableScroll("body")}
                  >
                  <table
                    ref={tableBodyTableRef}
                    className="stripe row-border order-column common-table table-striped main-table w-100 stock-adjustment-report-table"
                  >
                    <tbody>
                      {sortedList.length ? (
                        sortedList.map((result, key) => (
                          <tr key={result?.adjustment_id || key}>
                            <td className="fixed-side">{(currentPage - 1) * (Number(limit) || 0) + key + 1}</td>
                            <td className="fixed-side">
                              {result?.date ? formatDate(result?.date) : ""}
                            </td>
                            <td className="fixed-side link-color">
                              {result?.sa_no}
                            </td>
                            <td className="fixed-side">{result?.SKU}</td>
                            <td className="fixed-side">{result?.stock_id}</td>
                            <td className="fixed-side">
                              {result?.metal_name}
                            </td>
                            <td className="fixed-side">
                              {result?.stone_name}
                            </td>
                            <td className="fixed-side">{result?.size_name}</td>
                            <td className="fixed-side">{result?.qty}</td>
                            <td className="fixed-side">{result?.adj_qty}</td>
                            <td className="fixed-side">{result?.amount}</td>
                            <td className="fixed-side">{result?.location}</td>
                            <td className="fixed-side">
                              <label
                                className={
                                  STATUS_CLASS[
                                    (result?.status || "").toLowerCase()
                                  ] || ""
                                }
                              >
                                {result?.status}
                              </label>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className="bg-white">
                          <td
                            colSpan={HEAD_COLUMNS.length + 1}
                            className="text-center bg-white"
                          >
                            {t("No Data Available")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                        <td>{totalQty}</td>
                        <td>{totalAdjQty}</td>
                        <td>{toFormatPrice(totalAmount, { addSymbol: true })}</td>
                        <td />
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                  </div>
                  <div id="table_loader" className={datalodaer ? "mt-4" : "d-none"}>
                    <div className="dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
              </div>
              {reportlist?.length > 0 ? (
                <Pagination
                  total={totalPages}
                  current={currentPage}
                  pagination={(crPage) => handlePaginations(crPage)}
                />
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default StockAdjustmentReport;
